import {
  BILLING_PRODUCT_IDS,
  BILLING_PROTOCOL_VERSION,
  computeBillingStatus,
  emptyLedgerUsage,
  emptyPurchaseFacts,
  FREE_TRIAL_INSPECTIONS,
  normalizeRedeemCode,
  planActiveAt,
  RELEASE_LIMIT,
  RELEASE_WINDOW_DAYS,
  type BillingOkResponse,
  type BillingProductKey,
  type CoverageRecord,
  type HoldOutcome,
  type InspectionCoverage,
  type InspectionHolder,
  type LedgerUsage,
  type PurchaseFacts,
  type RedeemOutcome,
  type ReleaseOutcome
} from '../../supabase/functions/_shared/billingPolicy';
import type { BillingCallOutcome, BillingConsumeBody, BillingTransport } from '../backend/billingClient';
import type { PurchaseFailureCode, PurchasesAdapter, StoreEntitlement, StoreIdentityOutcome, StorePurchaseOutcome } from './purchasesContract';

// The fake purchase world: an App Store, a RevenueCat and a billing server that exist only in
// memory. It serves TWO callers:
//   - the tests, which drive purchases, restores, offer codes, failures and offline paths without a
//     device (tests/state/billingStore.test.ts, tests/ui/planPage.test.tsx);
//   - the browser dev server, where the plan page must be usable while the real store cannot be.
//
// It runs the REAL policy (supabase/functions/_shared/billingPolicy.ts) and mirrors what the SQL
// ledger does, so a flow that works here fails for product reasons, not for simulation reasons.
// It can never run in a shipped build: the selection in src/native/purchasesAdapter.ts and
// src/backend/billingClient.ts requires import.meta.env.DEV, which is false in every build.
const MONTH_MS = 30 * 24 * 60 * 60 * 1000;
const YEAR_MS = 365 * 24 * 60 * 60 * 1000;

export interface FakePurchaseRecord {
  id: string;
  product: BillingProductKey;
  at: number;
}

export interface FakePurchaseWorld {
  adapter: PurchasesAdapter;
  transport: BillingTransport;
  // Test / development controls.
  reset(): void;
  setConfigured(value: boolean): void;
  setOnline(value: boolean): void;
  setNextOutcome(outcome: 'purchased' | 'cancelled' | 'pending' | { failed: PurchaseFailureCode } | null): void;
  // Grants a purchase without the sheet — what a redeemed offer code looks like from the app's side.
  grant(product: BillingProductKey, at?: number): void;
  // Records AI use in 单 (Owner 2026-10-19), so 「还剩几单」 can be exercised.
  spend(input: { inspectionId?: string; poolUnits?: number; addOnUnits?: number; inspectionUnits?: number; inspectionMicros?: number }): void;
  // A redemption code the simulated server accepts (Owner item 46).
  addCode(code: string, units: number): void;
  // Mark this account's free inspections as already used (Owner 2026-09-11 「头三次免费」), so a
  // test can reach the state where a formal inspection has to be paid for.
  spendTrials(count: number): void;
  // An open formal inspection of this account that lives on ANOTHER device (billingPolicy.ts V-2), so the
  // 「别的设备上还有 N 单没做完」 notice can be exercised.
  openOnOtherDevice(inspectionId: string, holder: InspectionHolder, at?: number): void;
  state(): { purchases: FakePurchaseRecord[]; coverage: CoverageRecord[]; usage: LedgerUsage; appUserId: string | null; releases: number };
}

export function createFakePurchaseWorld(options: { now?: () => number } = {}): FakePurchaseWorld {
  const now = options.now ?? (() => Date.now());
  let purchases: FakePurchaseRecord[] = [];
  let coverage: CoverageRecord[] = [];
  let releases: number[] = [];
  let usage: LedgerUsage = emptyLedgerUsage();
  let codes = new Map<string, number>();
  let redeemed = new Set<string>();
  let appUserId: string | null = null;
  let configured = true;
  let isOnline = true;
  let nextOutcome: 'purchased' | 'cancelled' | 'pending' | { failed: PurchaseFailureCode } | null = null;
  let sequence = 0;
  const listeners = new Set<(entitlement: StoreEntitlement) => void>();

  function factsNow(): PurchaseFacts {
    const facts = emptyPurchaseFacts();
    const subscription = [...purchases].filter((p) => p.product === 'monthly' || p.product === 'annual').sort((a, b) => b.at - a.at)[0];
    if (subscription) {
      facts.plan = subscription.product === 'monthly' ? 'monthly' : 'annual';
      facts.planProductId = BILLING_PRODUCT_IDS[subscription.product];
      facts.periodStart = subscription.at;
      facts.activeUntil = subscription.at + (subscription.product === 'monthly' ? MONTH_MS : YEAR_MS);
      facts.willRenew = true;
    }
    facts.singlePurchases = purchases.filter((p) => p.product === 'single').map((p) => ({ id: p.id, at: p.at, sandbox: true }));
    facts.addOnPurchases = purchases.filter((p) => p.product === 'addOn').map((p) => ({ id: p.id, at: p.at, sandbox: true }));
    facts.managementUrl = 'https://apps.apple.com/account/subscriptions';
    return facts;
  }

  function entitlementNow(): StoreEntitlement {
    const facts = factsNow();
    const active = planActiveAt(facts, now());
    return {
      plan: active ? facts.plan : null,
      activeUntil: facts.activeUntil,
      willRenew: facts.willRenew,
      managementUrl: facts.managementUrl,
      singlePurchases: facts.singlePurchases.length,
      addOnPurchases: facts.addOnPurchases.length,
      appUserId
    };
  }

  function announce(): void {
    const entitlement = entitlementNow();
    for (const listener of [...listeners]) listener(entitlement);
  }

  function add(product: BillingProductKey, at: number): void {
    sequence += 1;
    purchases = [...purchases, { id: `fake_txn_${sequence}`, product, at }];
  }

  function recentReleases(): number {
    return releases.filter((at) => at > now() - RELEASE_WINDOW_DAYS * 24 * 60 * 60 * 1000).length;
  }

  function statusResponse(): BillingCallOutcome {
    const facts = factsNow();
    return {
      kind: 'ok',
      response: {
        ok: true,
        protocolVersion: BILLING_PROTOCOL_VERSION,
        status: computeBillingStatus({ facts, coverage: [...coverage], usage: { ...usage, releasesRecent: recentReleases() }, now: now(), internalTester: false })
      }
    };
  }

  function answerWith(extra: Partial<BillingOkResponse>): BillingCallOutcome {
    const status = statusResponse();
    return status.kind === 'ok' ? { kind: 'ok', response: { ...status.response, ...extra } } : status;
  }

  function setRecord(inspectionId: string, patch: Partial<CoverageRecord>): void {
    coverage = coverage.map((record) => (record.inspectionId === inspectionId ? { ...record, ...patch } : record));
  }

  // Mirrors billing_inspection_hold().
  function hold(inspectionId: string, device: InspectionHolder, take: boolean): HoldOutcome {
    const record = coverage.find((entry) => entry.inspectionId === inspectionId);
    if (!record) return 'notFound';
    if (record.closedAt) return 'closed';
    if (take) setRecord(inspectionId, { holder: device, discardedAt: null });
    else if (!record.holder) setRecord(inspectionId, { holder: device });
    return 'held';
  }

  // Mirrors billing_release_inspection() (the simulated server has no PDFs in the cloud).
  const discardReasons = new Map<string, ReleaseOutcome>();
  function release(inspectionId: string, device: InspectionHolder): { outcome: ReleaseOutcome; coverage: InspectionCoverage | null } {
    const record = coverage.find((entry) => entry.inspectionId === inspectionId);
    if (!record) return { outcome: 'notFound', coverage: null };
    if (record.discardedAt) return { outcome: discardReasons.get(inspectionId) ?? 'notCounted', coverage: record.coverage };
    if (record.closedAt) return { outcome: 'reported', coverage: record.coverage };
    if (record.holder && record.holder.deviceId !== device.deviceId) return { outcome: 'heldElsewhere', coverage: record.coverage };
    let reason: ReleaseOutcome | null = null;
    if (record.coverage !== 'single' && record.coverage !== 'trial') reason = 'notCounted';
    else if ((usage.inspectionUnits[inspectionId] ?? 0) > 0 || (usage.inspectionUsedMicros[inspectionId] ?? 0) > 0) reason = 'aiUsed';
    else if (recentReleases() >= RELEASE_LIMIT) reason = 'limitReached';
    if (reason) {
      discardReasons.set(inspectionId, reason);
      setRecord(inspectionId, { discardedAt: now() });
      return { outcome: reason, coverage: record.coverage };
    }
    releases = [...releases, now()];
    coverage = coverage.filter((entry) => entry.inspectionId !== inspectionId);
    return { outcome: 'released', coverage: record.coverage };
  }

  // Mirrors billing_consume_inspection(): idempotent per inspection, then plan → the oldest unspent
  // single purchase → a free inspection → unfunded.
  function consume(body: BillingConsumeBody): BillingCallOutcome {
    const existing = coverage.find((record) => record.inspectionId === body.inspectionId);
    if (existing) {
      const status = statusResponse();
      return status.kind === 'ok' ? { kind: 'ok', response: { ...status.response, coverage: existing.coverage, inspectionId: body.inspectionId } } : status;
    }
    const facts = factsNow();
    const at = body.clientCreatedAt ?? now();
    const planCovers = planActiveAt(facts, now()) || (facts.periodStart !== null && facts.activeUntil !== null && at >= facts.periodStart && at < facts.activeUntil);
    let kind: InspectionCoverage = 'unfunded';
    let transactionId: string | null = null;
    if (planCovers) kind = 'plan';
    else {
      const spent = new Set(coverage.filter((record) => record.coverage === 'single').map((record) => record.transactionId));
      const free = facts.singlePurchases.find((purchase) => !spent.has(purchase.id));
      if (free) {
        kind = 'single';
        transactionId = free.id;
      } else if (coverage.filter((record) => record.coverage === 'trial').length < FREE_TRIAL_INSPECTIONS) kind = 'trial';
    }
    coverage = [...coverage, { inspectionId: body.inspectionId, coverage: kind, transactionId, at: now() }];
    if (body.device) hold(body.inspectionId, body.device, false);
    const status = statusResponse();
    return status.kind === 'ok' ? { kind: 'ok', response: { ...status.response, coverage: kind, inspectionId: body.inspectionId } } : status;
  }

  const adapter: PurchasesAdapter = {
    platform: 'fake',
    configured: () => configured,
    async identify(userId: string): Promise<StoreIdentityOutcome> {
      if (!configured) return { ok: false, code: 'notConfigured', detail: 'the simulated store is switched off' };
      appUserId = userId;
      return { ok: true, entitlement: entitlementNow() };
    },
    async entitlement(): Promise<StoreEntitlement | null> {
      return configured ? entitlementNow() : null;
    },
    async purchase(product: BillingProductKey): Promise<StorePurchaseOutcome> {
      if (!configured) return { kind: 'failed', code: 'notConfigured', detail: 'the simulated store is switched off' };
      if (!isOnline) return { kind: 'failed', code: 'network', detail: 'offline' };
      const outcome = nextOutcome;
      nextOutcome = null;
      if (outcome === 'cancelled') return { kind: 'cancelled' };
      if (outcome === 'pending') return { kind: 'pending' };
      if (outcome && typeof outcome === 'object') return { kind: 'failed', code: outcome.failed, detail: 'simulated failure' };
      add(product, now());
      const entitlement = entitlementNow();
      announce();
      return { kind: 'purchased', entitlement };
    },
    async restore(): Promise<StoreIdentityOutcome> {
      if (!configured) return { ok: false, code: 'notConfigured', detail: 'the simulated store is switched off' };
      return { ok: true, entitlement: entitlementNow() };
    },
    async presentOfferCodeSheet() {
      if (!configured) return { ok: false, code: 'notConfigured' as PurchaseFailureCode, detail: 'the simulated store is switched off' };
      return { ok: true as const };
    },
    openManageSubscriptions(): boolean {
      return configured;
    },
    onEntitlementChange(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    }
  };

  const transport: BillingTransport = {
    async status() {
      if (!isOnline) return { kind: 'failed', code: 'offline', message: 'device is offline' };
      return statusResponse();
    },
    async consume(_token, body) {
      if (!isOnline) return { kind: 'failed', code: 'offline', message: 'device is offline' };
      return consume(body);
    },
    // Mirrors billing_redeem_code(): once per account and code; the gift 单 never expire.
    async redeem(_token, body) {
      if (!isOnline) return { kind: 'failed', code: 'offline', message: 'device is offline' };
      const code = normalizeRedeemCode(body.code);
      const units = code ? codes.get(code) : undefined;
      let outcome: RedeemOutcome = 'invalid';
      if (code && units !== undefined) {
        if (redeemed.has(code)) outcome = 'alreadyRedeemed';
        else {
          redeemed.add(code);
          usage = { ...usage, giftUnits: usage.giftUnits + units };
          outcome = 'redeemed';
        }
      }
      const status = statusResponse();
      return status.kind === 'ok' ? { kind: 'ok', response: { ...status.response, redeem: { outcome, units: outcome === 'redeemed' ? (units ?? 0) : 0 } } } : status;
    },
    async release(_token, body) {
      if (!isOnline) return { kind: 'failed', code: 'offline', message: 'device is offline' };
      return answerWith({ release: release(body.inspectionId, body.device), inspectionId: body.inspectionId });
    },
    // Mirrors billing_inspection_close().
    async close(_token, body) {
      if (!isOnline) return { kind: 'failed', code: 'offline', message: 'device is offline' };
      const found = coverage.some((record) => record.inspectionId === body.inspectionId);
      if (found) setRecord(body.inspectionId, { closedAt: coverage.find((record) => record.inspectionId === body.inspectionId)?.closedAt ?? now() });
      return answerWith({ closed: found, inspectionId: body.inspectionId });
    },
    async hold(_token, body) {
      if (!isOnline) return { kind: 'failed', code: 'offline', message: 'device is offline' };
      return answerWith({ hold: { outcome: hold(body.inspectionId, body.device, true) }, inspectionId: body.inspectionId });
    }
  };

  return {
    adapter,
    transport,
    reset() {
      purchases = [];
      coverage = [];
      releases = [];
      discardReasons.clear();
      usage = emptyLedgerUsage();
      codes = new Map();
      redeemed = new Set();
      appUserId = null;
      configured = true;
      isOnline = true;
      nextOutcome = null;
      sequence = 0;
      listeners.clear();
    },
    setConfigured(value) {
      configured = value;
    },
    setOnline(value) {
      isOnline = value;
    },
    setNextOutcome(outcome) {
      nextOutcome = outcome;
    },
    grant(product, at) {
      add(product, at ?? now());
      announce();
    },
    spend(input) {
      const id = input.inspectionId;
      usage = {
        ...usage,
        poolUnitsUsed: usage.poolUnitsUsed + (input.poolUnits ?? 0),
        addOnUnitsUsed: usage.addOnUnitsUsed + (input.addOnUnits ?? 0),
        inspectionUnits: id ? { ...usage.inspectionUnits, [id]: (usage.inspectionUnits[id] ?? 0) + (input.inspectionUnits ?? 0) } : { ...usage.inspectionUnits },
        inspectionUsedMicros: id ? { ...usage.inspectionUsedMicros, [id]: (usage.inspectionUsedMicros[id] ?? 0) + (input.inspectionMicros ?? 0) } : { ...usage.inspectionUsedMicros }
      };
    },
    addCode(code, units) {
      const normalized = normalizeRedeemCode(code);
      if (normalized) codes.set(normalized, units);
    },
    spendTrials(count) {
      for (let i = 0; i < count; i += 1) coverage = [...coverage, { inspectionId: 'trial_used_' + (coverage.length + 1), coverage: 'trial', transactionId: null, at: now() }];
    },
    openOnOtherDevice(inspectionId, holder, at) {
      coverage = [...coverage.filter((record) => record.inspectionId !== inspectionId), { inspectionId, coverage: 'plan', transactionId: null, at: at ?? now(), holder }];
    },
    state: () => ({ purchases: [...purchases], coverage: [...coverage], usage, appUserId, releases: releases.length })
  };
}

// A store that cannot transact here (a production web host). It never pretends: every answer says
// purchases are not available, and the UI states that instead of showing a dead button.
export const unavailablePurchasesAdapter: PurchasesAdapter = {
  platform: 'unavailable',
  configured: () => false,
  async identify() {
    return { ok: false, code: 'notNative', detail: 'purchases run in the iOS app' };
  },
  async entitlement() {
    return null;
  },
  async purchase() {
    return { kind: 'failed', code: 'notNative', detail: 'purchases run in the iOS app' };
  },
  async restore() {
    return { ok: false, code: 'notNative', detail: 'purchases run in the iOS app' };
  },
  async presentOfferCodeSheet() {
    return { ok: false, code: 'notNative', detail: 'purchases run in the iOS app' };
  },
  openManageSubscriptions() {
    return false;
  },
  onEntitlementChange() {
    return () => undefined;
  }
};

// The ONE simulated world the development server uses (store and billing server must agree).
let devWorld: FakePurchaseWorld | null = null;

// The browser dev server's simulated server knows one redemption code, so the web redeem card can be
// walked through end to end. It exists only in this in-memory simulation (never in a build).
export const DEV_REDEEM_CODE = 'YWSDEMO555';

export function devPurchaseWorld(): FakePurchaseWorld {
  if (!devWorld) {
    devWorld = createFakePurchaseWorld();
    devWorld.addCode(DEV_REDEEM_CODE, 5);
  }
  return devWorld;
}

export function resetDevPurchaseWorldForTests(): void {
  devWorld = null;
}
